import normalize from "jsonapi-normalizer";
import { makeAutoObservable } from "mobx";
import { UsersApi } from "../../../api/users/UsersApi";
import { UpdateUserInfo, DeleteUserInfo, CreateUserInfo } from "../../../api/typings/Users";
import { getUpdateUserData, getDeleteUserData, getCreateUserData } from "../../serializers/usersSerializer";
import { GetUsersData } from "../../stores/TableStore/TableStore";
import { NormalizedResources } from "../../typings/services";

export class UserService {
  constructor() {
    makeAutoObservable(this);
  }

  getUser = async (id: string): Promise<NormalizedResources> => {
    const response = await UsersApi.user(id, {
      include: [
        "profiles",
        "profiles.admin",
        "profiles.student",
        "profiles.tutor",
        "profiles.supervisor",
        "profiles.parent",
        "profiles.assistant",
        "profiles.contenter",
        "profiles.related_as_primary",
        "profiles.related_as_secondary",
        "profiles.related_as_primary.user",
        "profiles.related_as_secondary.user",
      ],
      with_trashed: "1",
    });
    return normalize(response).entities;
  };

  getUsers = async (data: GetUsersData): Promise<NormalizedResources> => {
    const response = await UsersApi.users({
      "page[number]": "1",
      "page[size]": "15",
      ...data,
      include: [
        "profiles",
        "profiles.admin",
        "profiles.student",
        "profiles.tutor",
        "profiles.supervisor",
        "profiles.parent",
        "profiles.assistant",
        "profiles.contenter",
      ],
    });
    const users = response.data!;
    const autoLoginUrls = Array.isArray(users)
      ? users.reduce<Record<string, string>>((result, user) => {
          if (user.meta) {
            result[user.id] = user.meta.autologin_url;
          } else {
            result[user.id] = "";
          }
          return result;
        }, {})
      : {
          [users.id]: users.meta!.autologin_url,
        };
    return {
      ...normalize(response).entities,
      autoLoginUrls,
      page: response.meta!.page,
    };
  };

  createUser = async (data: CreateUserInfo): Promise<Pick<NormalizedResources, "users" | "userMeta">> => {
    const createUserData = await getCreateUserData(data);
    const userResponse = await UsersApi.createUser(createUserData);
    return {
      ...normalize(userResponse).entities,
      userMeta: {
        password_plain: userResponse.data!.meta!.password_plain,
        autologin_url: userResponse.data!.meta!.autologin_url,
      },
    };
  };

  updateUser = async (data: UpdateUserInfo): Promise<Pick<NormalizedResources, "users">> => {
    const updateUserData = await getUpdateUserData(data);
    const response = await UsersApi.updateUser(data.id, updateUserData, { include: ["profiles"] });
    return normalize(response).entities;
  };

  restoreUser = async (data: DeleteUserInfo<null>): Promise<NormalizedResources> => {
    const deleteUserData = await getDeleteUserData<null>(data);
    const response = await UsersApi.restoreUser(data.id, deleteUserData, {
      include: [
        "profiles",
        "profiles.admin",
        "profiles.student",
        "profiles.tutor",
        "profiles.supervisor",
        "profiles.parent",
        "profiles.assistant",
        "profiles.contenter",
        "profiles.related_as_primary",
        "profiles.related_as_secondary",
        "profiles.related_as_primary.user",
        "profiles.related_as_secondary.user",
      ],
      with_trashed: "1",
    });
    return normalize(response).entities;
  };

  deleteUser = async (data: DeleteUserInfo): Promise<Pick<NormalizedResources, "users" | "users_profiles">> => {
    const deleteUserData = await getDeleteUserData(data);
    const response = await UsersApi.deleteUser(data.id, deleteUserData, {
      include: [
        "profiles",
        "profiles.admin",
        "profiles.student",
        "profiles.tutor",
        "profiles.supervisor",
        "profiles.parent",
        "profiles.assistant",
        "profiles.contenter",
        "profiles.related_as_primary",
        "profiles.related_as_secondary",
        "profiles.related_as_primary.user",
        "profiles.related_as_secondary.user",
      ],
      with_trashed: "1",
    });
    return normalize(response).entities;
  };
}
